import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  AlertTriangle,
  Trash2,
  Folder,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import { useModal } from "../../context/ModalContext";

const DeleteSectionModal = ({
  isOpen,
  onClose,
  onConfirm,
  section,
  sections = [],
  testCaseCount = 0,
  subsectionCount = 0,
}) => {
  const { incrementModalCount, decrementModalCount } = useModal();

  const [testCaseAction, setTestCaseAction] = useState("move"); // 'move' or 'delete' for test cases
  const [subsectionAction, setSubsectionAction] = useState("move"); // 'move' or 'delete' for subsections
  const [targetSectionId, setTargetSectionId] = useState("");
  const [targetSubsectionSectionId, setTargetSubsectionSectionId] =
    useState("");
  const [loading, setLoading] = useState(false);

  const [expandedSections, setExpandedSections] = useState(new Set());

  // Register/unregister modal with context
  useEffect(() => {
    if (isOpen) {
      incrementModalCount();
      return () => {
        decrementModalCount();
      };
    }
  }, [isOpen, incrementModalCount, decrementModalCount]);

  // Build tree structure from sections (handle both tree and flat list formats)
  const buildSectionTree = React.useCallback(
    (sectionList) => {
      if (!Array.isArray(sectionList) || sectionList.length === 0) return [];

      // Recursively filter out the section being deleted and its descendants
      const filterSection = (list, excludeId) => {
        if (!Array.isArray(list)) return [];

        return list
          .filter((s) => {
            const sectionId = s.section_id || s.folder_id;
            return sectionId && sectionId !== excludeId;
          })
          .map((s) => {
            const sectionId = s.section_id || s.folder_id;
            const hasChildren =
              s.children && Array.isArray(s.children) && s.children.length > 0;

            return {
              ...s,
              section_id: sectionId,
              parent_section_id:
                s.parent_section_id !== undefined
                  ? s.parent_section_id
                  : s.parent_folder_id,
              children: hasChildren ? filterSection(s.children, excludeId) : [],
            };
          });
      };

      // Check if already a tree structure (has nested children)
      const hasTreeStructure = sectionList.some((s) => {
        const hasDirectChildren =
          s.children && Array.isArray(s.children) && s.children.length > 0;
        if (hasDirectChildren) return true;
        // Check nested children
        if (s.children) {
          return s.children.some(
            (child) =>
              child.children &&
              Array.isArray(child.children) &&
              child.children.length > 0,
          );
        }
        return false;
      });

      if (hasTreeStructure) {
        // Already a tree, just filter out the section being deleted
        return filterSection(sectionList, section?.section_id);
      }

      // Build tree from flat list
      const sectionMap = new Map();
      const rootSections = [];
      const excludeId = section?.section_id;

      // First pass: create map of all sections
      sectionList.forEach((s) => {
        const sectionId = s.section_id || s.folder_id;
        const parentSectionId =
          s.parent_section_id !== undefined
            ? s.parent_section_id
            : s.parent_folder_id;

        if (sectionId && sectionId !== excludeId) {
          sectionMap.set(sectionId, {
            ...s,
            section_id: sectionId,
            parent_section_id: parentSectionId,
            children: [],
          });
        }
      });

      // Second pass: build hierarchy
      sectionList.forEach((s) => {
        const sectionId = s.section_id || s.folder_id;
        const parentSectionId =
          s.parent_section_id !== undefined
            ? s.parent_section_id
            : s.parent_folder_id;

        if (sectionId === excludeId || !sectionId) return; // Skip the section being deleted

        const sectionNode = sectionMap.get(sectionId);
        if (!sectionNode) return;

        if (parentSectionId && parentSectionId !== excludeId) {
          const parent = sectionMap.get(parentSectionId);
          if (parent) {
            parent.children.push(sectionNode);
          } else {
            // Parent not found or was excluded, treat as root
            rootSections.push(sectionNode);
          }
        } else {
          // No parent or parent is null - this is a root section
          rootSections.push(sectionNode);
        }
      });

      return rootSections;
    },
    [section],
  );

  // Filter out the section being deleted and its children from target options
  const availableSections = React.useMemo(() => {
    if (!Array.isArray(sections) || !section) return [];
    return buildSectionTree(sections);
  }, [sections, section, buildSectionTree]);

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Render section tree recursively - simple clean tree view
  const renderSectionTree = (sectionList, level = 0, parentPath = "") => {
    if (!Array.isArray(sectionList) || sectionList.length === 0) return null;

    return sectionList.map((s, index) => {
      const sectionId = s.section_id || s.folder_id;
      const hasChildren =
        s.children && Array.isArray(s.children) && s.children.length > 0;
      const isExpanded = expandedSections.has(sectionId);
      // Calculate indentation - significantly more space for better visibility
      const indentPerLevel = 56; // Space per nesting level - significantly increased for better visibility (56px = ~3.5rem)
      const basePadding = 16;
      const totalIndent = basePadding + level * indentPerLevel;
      const currentPath = parentPath ? `${parentPath} > ${s.name}` : s.name;
      const childCount = hasChildren ? s.children.length : 0;
      const isSelected =
        (subsectionCount > 0 &&
          subsectionAction === "move" &&
          targetSubsectionSectionId === sectionId) ||
        (testCaseCount > 0 &&
          testCaseAction === "move" &&
          targetSectionId === sectionId);

      return (
        <div key={sectionId}>
          <div
            className={`flex items-center gap-2 py-1.5 px-2 cursor-pointer transition-colors ${
              isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
            }`}
            style={{
              paddingLeft: `${totalIndent}px`,
            }}
            onClick={() => {
              const isSubsectionContext =
                subsectionCount > 0 && subsectionAction === "move";
              const isTestCaseContext =
                testCaseCount > 0 && testCaseAction === "move";

              if (isSubsectionContext) {
                setTargetSubsectionSectionId(sectionId);
              } else if (isTestCaseContext) {
                setTargetSectionId(sectionId);
              }
            }}
          >
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSection(sectionId);
                }}
                className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-5 flex-shrink-0" />
            )}
            <Folder className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <span className="flex-1 text-sm text-gray-700" title={currentPath}>
              {s.name}
            </span>
            {hasChildren && childCount > 0 && (
              <span className="text-xs text-gray-500">({childCount})</span>
            )}
            {isSelected && (
              <span className="text-xs text-indigo-600 font-medium">
                Selected
              </span>
            )}
          </div>
          {hasChildren && isExpanded && (
            <div>{renderSectionTree(s.children, level + 1, currentPath)}</div>
          )}
        </div>
      );
    });
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setTestCaseAction("move");
      setSubsectionAction("move");
      setTargetSectionId("");
      setTargetSubsectionSectionId("");
      // Expand all sections by default for better visibility
      const expandAll = (sectionList) => {
        const ids = new Set();
        if (Array.isArray(sectionList)) {
          sectionList.forEach((s) => {
            const sectionId = s.section_id;
            if (
              sectionId &&
              s.children &&
              Array.isArray(s.children) &&
              s.children.length > 0
            ) {
              ids.add(sectionId);
              const childIds = expandAll(s.children);
              childIds.forEach((id) => ids.add(id));
            }
          });
        }
        return ids;
      };
      setExpandedSections(expandAll(availableSections));
    }
  }, [isOpen, availableSections]);

  const handleConfirm = async () => {
    // Validate test case action if there are test cases
    if (testCaseCount > 0) {
      if (testCaseAction === "move" && !targetSectionId) {
        toast.error("Please select a target section to move test cases to");
        return;
      }
    }

    // Validate subsection action if there are subsections
    if (subsectionCount > 0) {
      if (subsectionAction === "move" && !targetSubsectionSectionId) {
        toast.error("Please select a target section to move subsections to");
        return;
      }
    }

    setLoading(true);
    try {
      const options = {};

      // Handle test cases
      if (testCaseCount > 0) {
        if (testCaseAction === "delete") {
          options.deleteTestCases = true;
        } else if (testCaseAction === "move" && targetSectionId) {
          options.moveToSectionId = targetSectionId;
        }
      }

      // Handle subsections
      if (subsectionCount > 0) {
        if (subsectionAction === "delete") {
          options.deleteSubsections = true;
        } else if (subsectionAction === "move" && targetSubsectionSectionId) {
          options.moveSubsectionsToSectionId = targetSubsectionSectionId;
        }
      }

      await onConfirm(section, options);
      onClose();
    } catch (error) {
      console.error("Error in delete confirmation:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!section) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Delete Section
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">
                Are you sure you want to delete section "{section.name}"?
              </p>
              <p className="text-xs text-red-700 mt-1.5">
                This action cannot be undone.
              </p>
            </div>
          </div>

          {subsectionCount > 0 && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold text-amber-900">
                  This section contains {subsectionCount} subsection
                  {subsectionCount !== 1 ? "s" : ""}.
                </p>
                <p className="text-xs text-amber-700 mt-1.5">
                  Please choose how to handle these subsections:
                </p>
              </div>

              <div className="space-y-3">
                <div
                  className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    subsectionAction === "move"
                      ? "border-indigo-500 bg-indigo-50 shadow-sm"
                      : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                  onClick={() => setSubsectionAction("move")}
                >
                  <input
                    type="radio"
                    id="subsection-move"
                    name="subsection-action"
                    value="move"
                    checked={subsectionAction === "move"}
                    onChange={() => setSubsectionAction("move")}
                    className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <Label
                    htmlFor="subsection-move"
                    className="flex-1 cursor-pointer"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        Move subsections to another section
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        All subsections will be moved to the selected section
                      </p>
                    </div>
                  </Label>
                </div>

                <div
                  className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    subsectionAction === "delete"
                      ? "border-red-500 bg-red-50 shadow-sm"
                      : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                  onClick={() => setSubsectionAction("delete")}
                >
                  <input
                    type="radio"
                    id="subsection-delete"
                    name="subsection-action"
                    value="delete"
                    checked={subsectionAction === "delete"}
                    onChange={() => setSubsectionAction("delete")}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500"
                  />
                  <Label
                    htmlFor="subsection-delete"
                    className="flex-1 cursor-pointer"
                  >
                    <div>
                      <p className="text-sm font-medium text-red-600">
                        Delete all subsections
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        All {subsectionCount} subsection
                        {subsectionCount !== 1 ? "s" : ""} will be permanently
                        deleted
                      </p>
                    </div>
                  </Label>
                </div>
              </div>

              {subsectionAction === "move" && (
                <div className="space-y-2 mt-3">
                  <Label
                    htmlFor="target-subsection-section"
                    className="text-sm font-semibold text-gray-700"
                  >
                    Select target section for subsections
                  </Label>
                  <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto bg-white shadow-inner">
                    {availableSections.length > 0 ? (
                      <div className="py-2 relative">
                        {renderSectionTree(availableSections)}
                      </div>
                    ) : (
                      <div className="p-6 text-center">
                        <Folder className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          No other sections available
                        </p>
                        <p className="text-xs text-gray-500">
                          You must either delete the subsections or create
                          another section first.
                        </p>
                      </div>
                    )}
                  </div>
                  {targetSubsectionSectionId && (
                    <p className="text-xs text-indigo-600">
                      Selected:{" "}
                      {(() => {
                        const findSection = (list, id) => {
                          for (const sec of list || []) {
                            if (sec.section_id === id) return sec;
                            if (sec.children) {
                              const found = findSection(sec.children, id);
                              if (found) return found;
                            }
                          }
                          return null;
                        };
                        return (
                          findSection(
                            availableSections,
                            targetSubsectionSectionId,
                          )?.name || "Unknown"
                        );
                      })()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {testCaseCount > 0 && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold text-amber-900">
                  This section contains {testCaseCount} test case
                  {testCaseCount !== 1 ? "s" : ""}.
                </p>
                <p className="text-xs text-amber-700 mt-1.5">
                  Please choose how to handle these test cases:
                </p>
              </div>

              <div className="space-y-3">
                <div
                  className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    testCaseAction === "move"
                      ? "border-indigo-500 bg-indigo-50 shadow-sm"
                      : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                  onClick={() => setTestCaseAction("move")}
                >
                  <input
                    type="radio"
                    id="testcase-move"
                    name="testcase-action"
                    value="move"
                    checked={testCaseAction === "move"}
                    onChange={() => setTestCaseAction("move")}
                    className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <Label
                    htmlFor="testcase-move"
                    className="flex-1 cursor-pointer"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        Move test cases to another section
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        All test cases will be moved to the selected section
                      </p>
                    </div>
                  </Label>
                </div>

                <div
                  className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    testCaseAction === "delete"
                      ? "border-red-500 bg-red-50 shadow-sm"
                      : "border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                  onClick={() => setTestCaseAction("delete")}
                >
                  <input
                    type="radio"
                    id="testcase-delete"
                    name="testcase-action"
                    value="delete"
                    checked={testCaseAction === "delete"}
                    onChange={() => setTestCaseAction("delete")}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500"
                  />
                  <Label
                    htmlFor="testcase-delete"
                    className="flex-1 cursor-pointer"
                  >
                    <div>
                      <p className="text-sm font-medium text-red-600">
                        Delete all test cases
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        All {testCaseCount} test case
                        {testCaseCount !== 1 ? "s" : ""} will be permanently
                        deleted
                      </p>
                    </div>
                  </Label>
                </div>
              </div>

              {testCaseAction === "move" && (
                <div className="space-y-2 mt-3">
                  <Label
                    htmlFor="target-section"
                    className="text-sm font-semibold text-gray-700"
                  >
                    Select target section for test cases
                  </Label>
                  <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto bg-white shadow-inner">
                    {availableSections.length > 0 ? (
                      <div className="py-2 relative">
                        {renderSectionTree(availableSections)}
                      </div>
                    ) : (
                      <div className="p-6 text-center">
                        <Folder className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          No other sections available
                        </p>
                        <p className="text-xs text-gray-500">
                          You must either delete the test cases or create
                          another section first.
                        </p>
                      </div>
                    )}
                  </div>
                  {targetSectionId && (
                    <p className="text-xs text-indigo-600">
                      Selected:{" "}
                      {(() => {
                        const findSection = (list, id) => {
                          for (const sec of list || []) {
                            if (sec.section_id === id) return sec;
                            if (sec.children) {
                              const found = findSection(sec.children, id);
                              if (found) return found;
                            }
                          }
                          return null;
                        };
                        return (
                          findSection(availableSections, targetSectionId)
                            ?.name || "Unknown"
                        );
                      })()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {testCaseCount === 0 && subsectionCount === 0 && (
            <p className="text-sm text-gray-600">
              This section has no test cases or subsections and can be safely
              deleted.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={
              loading ||
              (testCaseCount > 0 &&
                testCaseAction === "move" &&
                !targetSectionId) ||
              (subsectionCount > 0 &&
                subsectionAction === "move" &&
                !targetSubsectionSectionId)
            }
          >
            {loading ? "Deleting..." : "Delete Section"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteSectionModal;
